import React, { useEffect, useRef } from 'react';
import { RichText } from '@lexkit/editor';
import { Box } from '@mui/material';
import { applyStoredContentToEditor } from './rich-text-apply';
import {
  RichTextProvider,
  richTextContentSx,
  richTextEditorConfig,
  richTextExtensions,
  useRichTextEditor,
} from './rich-text-system';

interface RichTextEditorProps {
  /** Controlled value: a Lexical editor state serialized to JSON (or legacy plain text). */
  value: string;
  /** Called with the serialized JSON string whenever the content actually changes. */
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
}

function RichTextEditorInner({ value, onChange, placeholder, minRows = 3 }: RichTextEditorProps) {
  const { editor } = useRichTextEditor();
  // The value we consider "current" (raw stored string on load; emitted JSON after an edit). Used
  // to ignore the parent echoing our own onChange back as `value`.
  const appliedValueRef = useRef<string | null>(null);
  // Serialized state of the last loaded/emitted content, to suppress no-op updates (selection
  // changes, or the update caused by loading content) from being emitted as edits.
  const baselineJsonRef = useRef<string | null>(null);
  // True while we programmatically load content, so the resulting update updates the baseline
  // instead of being emitted as a user edit.
  const isApplyingRef = useRef(false);

  // Emit only real content changes, as serialized JSON.
  useEffect(() => {
    if (!editor) return undefined;
    return editor.registerUpdateListener(
      ({ editorState, dirtyElements, dirtyLeaves }: { editorState: { toJSON(): unknown }; dirtyElements: Map<string, unknown>; dirtyLeaves: Set<string> }) => {
        const json = JSON.stringify(editorState.toJSON());
        if (isApplyingRef.current) {
          baselineJsonRef.current = json; // programmatic load — new baseline, no emit
          return;
        }
        if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return; // selection-only
        if (json === baselineJsonRef.current) return; // no change vs baseline
        baselineJsonRef.current = json;
        appliedValueRef.current = json;
        onChange(json);
      }
    );
  }, [editor, onChange]);

  // Load the external value on mount and whenever it is re-seeded (e.g. report switch), ignoring
  // the parent echoing our own emitted JSON back to us.
  useEffect(() => {
    if (!editor) return;
    if (value === appliedValueRef.current) return;
    appliedValueRef.current = value;
    isApplyingRef.current = true;
    applyStoredContentToEditor(editor, value);
    baselineJsonRef.current = JSON.stringify(editor.getEditorState().toJSON());
    isApplyingRef.current = false;
  }, [editor, value]);

  return (
    <Box
      sx={{
        ...richTextContentSx,
        typography: 'body1',
        width: '100%',
        border: '1px solid rgba(0, 0, 0, 0.23)',
        borderRadius: 1,
        padding: '14px',
        cursor: 'text',
        '&:focus-within': {
          borderColor: 'primary.main',
          boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.primary.main}`
        },
        '& [contenteditable]': {
          outline: 'none',
          minHeight: `${minRows * 1.4375}em`
        }
      }}
    >
      <RichText placeholder={placeholder} />
    </Box>
  );
}

/**
 * Plain-text editor for report comments, built on the same LexKit system as {@link RichTextView} but
 * editable. Deliberately renders **no toolbar**, so editors can
 * change the wording but cannot easily add styling. Content stays in the shared serialized-Lexical-JSON
 * format via the controlled `value`/`onChange` (JSON string) contract — so edits preserve any
 * formatting authored in the Gold app instead of flattening it, and legacy plain-text comments are
 * converted to JSON the first time they are edited and saved.
 */
export function RichTextEditor(props: RichTextEditorProps) {
  return (
    <RichTextProvider extensions={richTextExtensions} config={richTextEditorConfig}>
      <RichTextEditorInner {...props} />
    </RichTextProvider>
  );
}

export default RichTextEditor;
